import { motion, useReducedMotion } from "framer-motion";
import { Check, Sparkles, X, Zap } from "lucide-react";
import { cn, formatINR } from "@/lib/utils";
import { DescriptionTypeahead } from "./DescriptionTypeahead";
import { NumberPad } from "./NumberPad";
import {
  calcPendingLabel,
  formatAmountForDisplay,
  type CalcKey,
  type CalcState,
} from "./amountPad";
import {
  accountPrimaryLabel,
  categoryColor,
  type PickerAccount,
} from "./pickerHelpers";
import type { Category } from "@/components/categories/categoryHelpers";
import type { SuggestionTemplate } from "@/lib/suggestions/service";

export type EntryType = "income" | "expense";
export type DateMode = "today" | "yesterday" | "pick";

export interface MobileEntryScreenProps {
  type: EntryType;
  onTypeChange: (t: EntryType) => void;

  calc: CalcState;
  onPadKey: (key: CalcKey) => void;
  onAmountKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onAmountPaste: (e: React.ClipboardEvent<HTMLInputElement>) => void;

  category: Category | null;
  onOpenCategoryPicker: () => void;

  account: PickerAccount | null;
  onOpenAccountPicker: () => void;

  description: string;
  onDescriptionChange: (v: string) => void;

  dateMode: DateMode;
  onDateModeChange: (m: DateMode) => void;
  pickedDate: string;
  onPickedDateChange: (v: string) => void;
  date: string;

  paymentMethod: string;
  onPaymentMethodChange: (v: string) => void;
  paymentMethods: ReadonlyArray<{ value: string; label: string }>;

  suggestions: SuggestionTemplate[];
  onApplyTemplate: (t: SuggestionTemplate) => void;
  onApplyAndSaveTemplate: (t: SuggestionTemplate) => void;

  canSave: boolean;
  /** Why saving is blocked (F7: remark / sub-category), shown under the note. */
  blockedReason?: string | null;
  isSaving: boolean;
  onSave: () => void;
  onClose: () => void;
}

const TYPES: ReadonlyArray<{ value: EntryType; label: string }> = [
  { value: "income", label: "Income" },
  { value: "expense", label: "Expense" },
];

/**
 * Full-screen, zero-scroll entry screen for phones — the Wallet-style layout
 * the user asked for.
 *
 * Everything needed to record a transaction is on screen simultaneously:
 * save is in a persistent top bar rather than below a scrolling form, type is
 * a full-width coloured segmented control, account and category are one-line
 * tap targets that push a picker instead of an inline chip grid, and the
 * calculator keypad occupies the space the OS keyboard used to steal.
 *
 * The layout is a flex column pinned to `100dvh`: header, tabs, field rows,
 * templates and the pad are all fixed-height and `shrink-0`; the amount hero
 * is the only flexible band, so it absorbs whatever is left and the screen
 * can never overflow into a scroll.
 */
export function MobileEntryScreen(props: MobileEntryScreenProps) {
  const {
    type,
    onTypeChange,
    calc,
    onPadKey,
    onAmountKeyDown,
    onAmountPaste,
    category,
    onOpenCategoryPicker,
    account,
    onOpenAccountPicker,
    description,
    onDescriptionChange,
    dateMode,
    onDateModeChange,
    pickedDate,
    onPickedDateChange,
    date,
    paymentMethod,
    onPaymentMethodChange,
    paymentMethods,
    suggestions,
    onApplyTemplate,
    onApplyAndSaveTemplate,
    canSave,
    blockedReason,
    isSaving,
    onSave,
    onClose,
  } = props;

  const shouldReduceMotion = useReducedMotion();
  const income = type === "income";
  // Colour is the primary type signal, as in the reference — the whole upper
  // region takes the type's hue, not just a small toggle.
  const tint = income ? "var(--cp-emerald-wash)" : "var(--cp-rose-wash)";
  const ink = income ? "var(--cp-emerald)" : "var(--cp-rose)";
  const pending = calcPendingLabel(calc);

  return (
    <div className="flex h-[100dvh] flex-col bg-[var(--cp-canvas)]">
      {/* Persistent action bar — Save never scrolls away. */}
      <header
        className="flex h-12 shrink-0 items-center justify-between px-1"
        style={{ backgroundColor: tint }}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Cancel entry"
          className="flex h-11 w-11 items-center justify-center rounded-lg text-[var(--cp-ink-2)] transition-colors active:scale-95"
        >
          <X className="h-6 w-6" />
        </button>
        <span className="text-sm font-semibold" style={{ color: ink }}>
          {income ? "New income" : "New expense"}
        </span>
        <button
          type="button"
          onClick={onSave}
          disabled={!canSave || isSaving}
          aria-label="Save entry"
          className={cn(
            "flex h-11 w-11 items-center justify-center rounded-lg transition-all active:scale-95",
            canSave && !isSaving ? "opacity-100" : "opacity-35",
          )}
          style={{ color: ink }}
        >
          <Check className="h-6 w-6" strokeWidth={3} />
        </button>
      </header>

      {/* Type tabs — full width, three-equal-segment geometry with two types. */}
      <div className="grid shrink-0 grid-cols-2" style={{ backgroundColor: tint }}>
        {TYPES.map((t) => {
          const active = type === t.value;
          return (
            <button
              key={t.value}
              type="button"
              onClick={() => onTypeChange(t.value)}
              aria-pressed={active}
              className={cn(
                "relative flex h-11 items-center justify-center text-sm font-semibold uppercase tracking-wide transition-colors",
                active ? "" : "text-[var(--cp-ink-3)]",
              )}
              style={active ? { color: ink } : undefined}
            >
              {t.label}
              {active && (
                <motion.span
                  layoutId="entry-type-underline"
                  transition={
                    shouldReduceMotion ? { duration: 0 } : { type: "spring", damping: 30, stiffness: 400 }
                  }
                  className="absolute inset-x-4 bottom-0 h-[3px] rounded-full"
                  style={{ backgroundColor: ink }}
                  aria-hidden
                />
              )}
            </button>
          );
        })}
      </div>

      {/* Amount hero. Flexible, but capped: past ~190px the extra height is
          dead pink space, and it is worth more as taller keypad keys. */}
      <div
        className="flex min-h-[92px] max-h-[190px] flex-1 flex-col justify-center px-4"
        style={{ backgroundColor: tint }}
      >
        <div className="h-5 text-right text-sm tabular-nums text-[var(--cp-ink-3)]">
          {pending}
        </div>
        <div className="flex items-baseline gap-2">
          <span className="text-3xl font-semibold" style={{ color: ink }} aria-hidden>
            {income ? "+" : "−"}
          </span>
          {/* Read-only on purpose: it suppresses the OS keyboard on iOS and
              Android while staying focusable and labelled, so nothing below
              is ever covered. The pad drives it through the same reducer a
              physical keyboard does. */}
          <input
            type="text"
            inputMode="none"
            readOnly
            autoFocus
            value={formatAmountForDisplay(calc.entry)}
            onKeyDown={onAmountKeyDown}
            onPaste={onAmountPaste}
            placeholder="0"
            aria-label="Amount in rupees"
            className="min-w-0 flex-1 bg-transparent text-right text-[44px] font-bold leading-none tabular-nums text-[var(--cp-ink)] caret-transparent placeholder:text-[var(--cp-ink-3)] placeholder:opacity-40 focus:outline-none"
          />
          <span className="text-sm font-semibold text-[var(--cp-ink-3)]">INR</span>
        </div>
      </div>

      {/* Account + Category — one tap each, no inline chip list. */}
      <div className="grid shrink-0 grid-cols-2 divide-x divide-[var(--cp-line)] border-y border-[var(--cp-line)] bg-[var(--cp-surface)]">
        <button
          type="button"
          onClick={onOpenAccountPicker}
          aria-label={`Account: ${account ? accountPrimaryLabel(account) : "none"}`}
          className="flex min-h-[52px] flex-col justify-center px-3 py-1.5 text-left transition-colors active:bg-[var(--cp-surface-2)]"
        >
          <span className="text-[10px] font-semibold uppercase tracking-wider text-[var(--cp-ink-3)]">
            Account
          </span>
          <span className="truncate text-sm font-medium text-[var(--cp-ink)]">
            {account ? accountPrimaryLabel(account) : "No account"}
          </span>
        </button>
        <button
          type="button"
          onClick={onOpenCategoryPicker}
          aria-label={`Category: ${category ? category.name : "none selected"}`}
          className="flex min-h-[52px] flex-col justify-center px-3 py-1.5 text-left transition-colors active:bg-[var(--cp-surface-2)]"
        >
          <span className="text-[10px] font-semibold uppercase tracking-wider text-[var(--cp-ink-3)]">
            Category
          </span>
          <span className="flex items-center gap-1.5">
            {category && (
              <span
                className="h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: categoryColor(category) }}
                aria-hidden
              />
            )}
            <span
              className={cn(
                "truncate text-sm font-medium",
                category ? "text-[var(--cp-ink)]" : "text-[var(--cp-ink-3)]",
              )}
            >
              {category ? category.name : "Select category"}
            </span>
          </span>
        </button>
      </div>

      {/* Description. */}
      <div className="shrink-0 px-3 pt-2">
        <DescriptionTypeahead
          type={type}
          value={description}
          onChange={onDescriptionChange}
          onSelectTemplate={onApplyTemplate}
          placeholder="Remark (required)"
          inputClassName="h-10 border-[var(--cp-line)] bg-[var(--cp-surface)] text-[var(--cp-ink)] placeholder:text-[var(--cp-ink-3)] focus:border-[var(--cp-accent)]"
        />
        {blockedReason && (
          <p role="status" className="mt-1 text-[12px] font-medium text-[var(--cp-rose)]">
            {blockedReason}
          </p>
        )}
      </div>

      {/* Date + payment method on one row. */}
      <div className="flex shrink-0 items-center gap-1.5 px-3 pt-2">
        <div className="flex gap-1" role="group" aria-label="Date">
          {(
            [
              ["today", "Today"],
              ["yesterday", "Yest."],
              ["pick", "Date"],
            ] as const
          ).map(([mode, label]) => (
            <button
              key={mode}
              type="button"
              onClick={() => onDateModeChange(mode)}
              aria-pressed={dateMode === mode}
              className={cn(
                "min-h-10 rounded-full border px-2.5 text-xs font-medium transition-colors active:scale-[0.97]",
                dateMode === mode
                  ? "border-[var(--cp-accent)] bg-[var(--cp-accent-wash)] text-[var(--cp-accent-ink)]"
                  : "border-[var(--cp-line)] bg-[var(--cp-surface)] text-[var(--cp-ink-2)]",
              )}
            >
              {label}
            </button>
          ))}
        </div>
        {dateMode === "pick" ? (
          <input
            type="date"
            value={pickedDate || date}
            onChange={(e) => onPickedDateChange(e.target.value)}
            aria-label="Entry date"
            className="min-h-10 min-w-0 flex-1 rounded-lg border border-[var(--cp-line)] bg-[var(--cp-surface)] px-2 text-xs text-[var(--cp-ink)]"
          />
        ) : (
          type === "expense" && (
            <select
              value={paymentMethod}
              onChange={(e) => onPaymentMethodChange(e.target.value)}
              aria-label="Payment method"
              className="min-h-10 min-w-0 flex-1 rounded-lg border border-[var(--cp-line)] bg-[var(--cp-surface)] px-2 text-xs text-[var(--cp-ink)]"
            >
              {paymentMethods.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          )
        )}
      </div>

      {/* Templates. First thing to give if vertical space runs out — never
          the save button and never the account/category row. */}
      {suggestions.length > 0 && (
        <div className="hidden shrink-0 px-3 pt-2 [@media(min-height:720px)]:block">
          <p className="mb-1 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-[var(--cp-ink-3)]">
            <Sparkles className="h-3 w-3" aria-hidden /> Templates
          </p>
          <div className="flex gap-1.5 overflow-x-auto scrollbar-none">
            {suggestions.map((s) => (
              <span
                key={`${s.categoryId}-${s.description}`}
                className="flex shrink-0 items-stretch overflow-hidden rounded-full border border-[var(--cp-line)] bg-[var(--cp-surface)]"
              >
                <button
                  type="button"
                  onClick={() => onApplyTemplate(s)}
                  className="flex min-h-10 items-center gap-1.5 px-2.5 text-xs font-medium text-[var(--cp-ink-2)] transition-colors active:bg-[var(--cp-surface-2)]"
                >
                  <span
                    className="h-2 w-2 shrink-0 rounded-full"
                    style={{ backgroundColor: s.category.color ?? "#94a3b8" }}
                    aria-hidden
                  />
                  <span className="max-w-[8rem] truncate">{s.description}</span>
                  <span className="text-[var(--cp-ink-3)]">{formatINR(s.amountPaise)}</span>
                </button>
                {/* One-tap save: a template already carries amount, category,
                    account and description, so it is submittable as-is. */}
                <button
                  type="button"
                  onClick={() => onApplyAndSaveTemplate(s)}
                  disabled={isSaving}
                  aria-label={`Save ${s.description} now`}
                  className="flex min-h-10 w-9 items-center justify-center border-l border-[var(--cp-line)] text-[var(--cp-accent-ink)] transition-colors active:bg-[var(--cp-accent-wash)] disabled:opacity-40"
                >
                  <Zap className="h-3.5 w-3.5" />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Calculator keypad, pinned to the bottom and taking every pixel the
          rows above did not claim. */}
      <div className="flex min-h-[264px] flex-1 px-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] pt-2">
        <NumberPad variant="calculator" onKey={onPadKey} className="h-full w-full" />
      </div>
    </div>
  );
}
